import { useState, useEffect, useCallback, useRef } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { RefreshCw, Search, Download, Trash2, Archive, AlertTriangle, AlertCircle, Info, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import api from '@/lib/api';

export default function GpuLogs() {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const timerRef = useRef(null);

  const loadDates = useCallback(async () => {
    try {
      const res = await api.get('/admin/gpu-logs/dates');
      setDates(res.data.dates || []);
      if (!selectedDate && res.data.dates?.length > 0) {
        setSelectedDate(res.data.dates[0].date);
      }
    } catch {}
  }, [selectedDate]);

  const loadStats = useCallback(async (date) => {
    if (!date) return;
    try {
      const res = await api.get('/admin/gpu-logs/stats', { params: { date } });
      setStats(res.data);
    } catch {}
  }, []);

  const loadLogs = useCallback(async (date, lvl, srch, pg) => {
    if (!date) return;
    setLoading(true);
    try {
      const params = { date, page: pg, per_page: 100 };
      if (lvl && lvl !== 'all') params.level = lvl;
      if (srch) params.search = srch;
      const res = await api.get('/admin/gpu-logs', { params });
      setLogs(res.data.lines || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load logs');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDates(); }, [loadDates]);

  useEffect(() => {
    if (selectedDate) {
      loadStats(selectedDate);
      loadLogs(selectedDate, level, search, page);
    }
  }, [selectedDate, level, search, page, loadStats, loadLogs]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && selectedDate) {
      timerRef.current = setInterval(() => {
        loadLogs(selectedDate, level, search, page);
        loadStats(selectedDate);
      }, refreshInterval * 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, refreshInterval, selectedDate, level, search, page, loadLogs, loadStats]);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };

  const handleDelete = async () => {
    if (!selectedDate) return;
    if (!window.confirm(`Delete all logs for ${selectedDate}? This cannot be undone.`)) return;
    try {
      await api.delete('/admin/gpu-logs', { params: { date: selectedDate } });
      toast.success(`Logs for ${selectedDate} deleted`);
      loadDates();
      setLogs([]);
      setStats(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleArchive = async () => {
    const before = prompt('Archive logs older than (YYYY-MM-DD):', selectedDate);
    if (!before) return;
    try {
      const res = await api.post('/api/admin/gpu-logs/archive', null, { params: { before } });
      toast.success(`Archived ${res.data.count} log file(s)`);
      loadDates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Archive failed'); }
  };

  const getLineColor = (line) => {
    if (line.includes('[ERROR]')) return 'text-red-400';
    if (line.includes('[WARNING]')) return 'text-amber-400';
    return 'text-slate-300';
  };

  const getLineBg = (line) => {
    if (line.includes('[ERROR]')) return 'bg-red-500/5';
    if (line.includes('[WARNING]')) return 'bg-amber-500/5';
    return '';
  };

  return (
    <div data-testid="gpu-logs-page" className="space-y-4">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">GPU Server Logs</h2>
        <p className="text-muted-foreground mt-1 text-sm">Monitor GPU server activity, errors, and performance in real-time.</p>
      </div>

      {/* Controls */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Date picker */}
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground">Date</span>
              <Select value={selectedDate} onValueChange={v => { setSelectedDate(v); setPage(1); }}>
                <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Select date" /></SelectTrigger>
                <SelectContent>
                  {dates.map(d => (
                    <SelectItem key={d.date} value={d.date}>{d.date} ({(d.size_bytes / 1024).toFixed(1)} KB)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Level filter */}
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground">Level</span>
              <Select value={level} onValueChange={v => { setLevel(v); setPage(1); }}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="error">Errors only</SelectItem>
                  <SelectItem value="warning">Warnings only</SelectItem>
                  <SelectItem value="info">Info only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="space-y-1 flex-1 min-w-48">
              <span className="text-[10px] text-muted-foreground">Search</span>
              <div className="flex gap-1">
                <Input className="h-8 text-xs" placeholder="Filter logs... (e.g., 500, timeout, docling)" value={searchInput}
                  onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
                <Button size="sm" variant="outline" className="h-8 px-2" onClick={handleSearch}><Search className="w-3 h-3" /></Button>
              </div>
            </div>

            {/* Auto-refresh */}
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground">Auto-refresh</span>
              <div className="flex items-center gap-2">
                <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
                {autoRefresh && (
                  <Select value={String(refreshInterval)} onValueChange={v => setRefreshInterval(Number(v))}>
                    <SelectTrigger className="w-16 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5s</SelectItem>
                      <SelectItem value="10">10s</SelectItem>
                      <SelectItem value="30">30s</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-1">
              <span className="text-[10px] text-muted-foreground">Actions</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { loadLogs(selectedDate, level, search, page); loadStats(selectedDate); }}>
                  <RefreshCw className="w-3 h-3 mr-1" />Refresh
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleArchive}>
                  <Archive className="w-3 h-3 mr-1" />Archive
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs border-destructive/30 text-destructive hover:bg-destructive/10" onClick={handleDelete}>
                  <Trash2 className="w-3 h-3 mr-1" />Delete
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats bar */}
      {stats && (
        <div className="flex gap-3 flex-wrap">
          <Badge variant="outline" className="text-xs py-1 px-3"><Info className="w-3 h-3 mr-1 text-blue-400" />{stats.total} total lines</Badge>
          <Badge variant="outline" className={`text-xs py-1 px-3 ${stats.errors > 0 ? 'border-red-500/40 text-red-400' : ''}`}><AlertCircle className="w-3 h-3 mr-1" />{stats.errors} errors</Badge>
          <Badge variant="outline" className={`text-xs py-1 px-3 ${stats.warnings > 0 ? 'border-amber-500/40 text-amber-400' : ''}`}><AlertTriangle className="w-3 h-3 mr-1" />{stats.warnings} warnings</Badge>
          <Badge variant="outline" className="text-xs py-1 px-3">{stats.info} info</Badge>
          {autoRefresh && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Live — refreshing every {refreshInterval}s</Badge>}
        </div>
      )}

      {/* Log viewer */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">{selectedDate || 'No date selected'} — {total} lines {search && `(filtered: "${search}")`}</CardTitle>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div data-testid="log-viewer" className="font-mono text-[11px] leading-relaxed max-h-[60vh] overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-sm text-muted-foreground p-6 text-center">{loading ? 'Loading...' : 'No logs found for the selected filters.'}</p>
            ) : (
              logs.map((line, i) => (
                <div key={i} className={`px-4 py-0.5 border-b border-border/5 hover:bg-secondary/20 ${getLineBg(line)}`}>
                  <span className={getLineColor(line)}>{line}</span>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} lines)</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-3 h-3 mr-1" />Previous
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next<ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
